import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
    AbstractControl,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { tap } from "rxjs/operators";
import enUS from "../../i18n/en-US.json";
import esES from "../../i18n/es-ES.json";
import ptBR from "../../i18n/pt-BR.json";

@Component({
    selector: "app-password-validator",
    templateUrl: "./password-validator.component.html",
    styleUrls: ["./password-validator.component.scss"]
})
export class PasswordValidatorComponent implements OnInit {
    @Input() passwordRegex: string;
    @Input() passwordCustomRules: string;

    @Output() validValue = new EventEmitter<boolean>();

    @Output() password = new EventEmitter<string>();

    public senhaForm: UntypedFormGroup;
    public customRules: string[] = [];

    constructor(private translate: TranslateService) {
        this.translate.setTranslation("pt-BR", ptBR, true);
        this.translate.setTranslation("en-US", enUS, true);
        this.translate.setTranslation("es-ES", esES, true);

        this.senhaForm = new UntypedFormGroup({
            senha: new UntypedFormControl("", [
                Validators.required,
                Validators.minLength(8),
                this.MatchPassword,
                this.getPasswordValidators()
            ]),
            confirmaSenha: new UntypedFormControl("", [
                Validators.required,
                this.MatchPassword,
                Validators.minLength(8)
            ])
        });

        this.senhaForm.valueChanges
            .pipe(
                tap(() => {
                    const isFormValid = this.isFormValid();
                    this.validValue.emit(isFormValid);
                    this.password.emit(this.senhaForm.controls["senha"].value);
                    
                    // Limpa erros dos campos quando a validação customizada está passando
                    this.clearErrorsIfCustomValidationPasses();
                })
            )
            .subscribe();
    }

    ngOnInit(): void {
        if (this.passwordCustomRules) {
            // Divide as regras por quebra de linha ou ponto e vírgula
            this.customRules = this.passwordCustomRules
                .split(/[\n;]/) 
                .map(rule => rule.trim())
                .filter(rule => rule.length > 0);
        }
    }

    // Limpa erros dos campos quando a validação customizada está passando
    private clearErrorsIfCustomValidationPasses(): void {
        const senhaControl = this.senhaForm.get('senha');
        const confirmaSenhaControl = this.senhaForm.get('confirmaSenha');
        
        if (!senhaControl?.value || !confirmaSenhaControl?.value) {
            return;
        }
        
        // Se a validação customizada está passando, limpa os erros relacionados à senha
        if (this.isPasswordValid()) {
            // Remove erros específicos de validação de senha, mas mantém outros erros
            const currentErrors = senhaControl.errors;
            if (currentErrors) {
                delete currentErrors['minlength'];
                delete currentErrors['customPasswordPattern'];
                delete currentErrors['notUppercase'];
                delete currentErrors['notSpecialCharacter'];
                
                // Se não há mais erros, define como null
                const remainingErrors = Object.keys(currentErrors).length > 0 ? currentErrors : null;
                senhaControl.setErrors(remainingErrors);
            }
        }
    }
    
    // Verifica se a senha atende aos critérios de validação
    private isPasswordValid(): boolean {
        const senhaControl = this.senhaForm.get('senha');
        if (!senhaControl?.value) {
            return false;
        }
        
        const senha = senhaControl.value;
        
        // Se há regras customizadas, verifica se a regex customizada é atendida
        if (this.passwordCustomRules && this.customRules.length > 0 && this.passwordRegex) {
            try {
                const regex = new RegExp(this.passwordRegex);
                return regex.test(senha);
            } catch (error) {
                return false;
            }
        }
        
        // Se há apenas regex customizada (sem regras customizadas)
        if (this.passwordRegex && (!this.passwordCustomRules || this.customRules.length === 0)) {
            try {
                const regex = new RegExp(this.passwordRegex);
                return regex.test(senha);
            } catch (error) {
                return false;
            }
        }
        
        // Validações padrão quando não há regex customizada
        if (!this.passwordRegex && (!this.passwordCustomRules || this.customRules.length === 0)) {
            const hasMinLength = senha.length >= 8;
            const hasUppercase = /[A-Z]/.test(senha);
            const hasSpecialChar = /[!@#$%^&*]/.test(senha);
            
            return hasMinLength && hasUppercase && hasSpecialChar;
        }
        
        return true;
    }

    // Verifica se o formulário está válido considerando as regras customizadas
    private isFormValid(): boolean {
        const senhaControl = this.senhaForm.get('senha');
        const confirmaSenhaControl = this.senhaForm.get('confirmaSenha');
        
        // Verifica se os campos básicos estão válidos
        if (!senhaControl?.value || !confirmaSenhaControl?.value) {
            return false;
        }
        
        // Verifica se as senhas coincidem
        if (senhaControl.value !== confirmaSenhaControl.value) {
            return false;
        }
        
        // Se há regras customizadas, verifica se a regex customizada é atendida
        if (this.passwordCustomRules && this.customRules.length > 0 && this.passwordRegex) {
            try {
                const regex = new RegExp(this.passwordRegex);
                return regex.test(senhaControl.value);
            } catch (error) {
                return false;
            }
        }
        
        // Se há apenas regex customizada (sem regras customizadas)
        if (this.passwordRegex && (!this.passwordCustomRules || this.customRules.length === 0)) {
            try {
                const regex = new RegExp(this.passwordRegex);
                return regex.test(senhaControl.value);
            } catch (error) {
                return false;
            }
        }
        
        // Validações padrão quando não há regex customizada
        if (!this.passwordRegex && (!this.passwordCustomRules || this.customRules.length === 0)) {
            const senha = senhaControl.value;
            const hasMinLength = senha.length >= 8;
            const hasUppercase = /[A-Z]/.test(senha);
            const hasSpecialChar = /[!@#$%^&*]/.test(senha);
            
            return hasMinLength && hasUppercase && hasSpecialChar;
        }
        
        return this.senhaForm.valid;
    }

    // Verifica se uma regra específica está sendo atendida
    isRuleValid(ruleIndex: number): boolean {
        const senha = this.senhaForm.get('senha')?.value;
        if (!senha || this.senhaForm.get('senha')?.hasError('required')) {
            return false;
        }

        // Se há regras customizadas, valida usando a regex customizada
        if (this.passwordCustomRules && this.customRules.length > 0 && this.passwordRegex) {
            try {
                const regex = new RegExp(this.passwordRegex);
                return regex.test(senha);
            } catch (error) {
                return false;
            }
        }

        // Validações padrão baseadas no índice da regra
        switch (ruleIndex) {
            case 0: // Mínimo 8 caracteres
                return senha.length >= 8;
            case 1: // Pelo menos uma letra maiúscula
                return /[A-Z]/.test(senha);
            case 2: // Pelo menos um caractere especial
                return /[!@#$%^&*]/.test(senha);
            default:
                return true;
        }
    }

    private MatchPassword(AC: UntypedFormControl) {
        const dataForm = AC.parent;
        if (!dataForm) return null;

        const password = dataForm.get("senha").value;
        const confirmPassword = dataForm.get("confirmaSenha").value;

        if (password != confirmPassword) {
            dataForm.controls["confirmaSenha"].setErrors({
                MatchPassword: true
            });

            if (dataForm.controls["confirmaSenha"] == AC) {
                return { MatchPassword: true };
            }
        } else {
            dataForm.controls["confirmaSenha"].setErrors(null);
        }
        return null;
    }

    public getPasswordValidators(): ValidatorFn {
        if (this.passwordRegex) {
            return this.customPasswordValidator(this.passwordRegex);
        }

        // Fallback para validações padrão se não houver regex customizada
        return (control: AbstractControl): ValidationErrors | null => {
            const errors = {};

            const uppercaseError = uppercaseValidator(control);
            if (uppercaseError) {
                Object.assign(errors, uppercaseError);
            }

            const specialCharError = specialCharacterValidator(control);
            if (specialCharError) {
                Object.assign(errors, specialCharError);
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    public customPasswordValidator(regex: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            try {
                const passwordRegex = new RegExp(regex);
                if (passwordRegex.test(control.value)) {
                    return null;
                }
                return { customPasswordPattern: true };
            } catch (error) {
                // Se a regex for inválida, usar validações padrão
                console.warn("Invalid password regex pattern:", regex);
                const errors = {};

                const uppercaseError = uppercaseValidator(control);
                if (uppercaseError) {
                    Object.assign(errors, uppercaseError);
                }

                const specialCharError = specialCharacterValidator(control);
                if (specialCharError) {
                    Object.assign(errors, specialCharError);
                }

                return Object.keys(errors).length > 0 ? errors : null;
            }
        };
    }
}

export const uppercaseValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control) {
        return null;
    }

    const password = control.parent.get("senha");
    const uppercaseRegex = new RegExp("^(?=.*[A-Z])");

    if (uppercaseRegex.test(password.value)) return null;

    return { notUppercase: true };
};

export const specialCharacterValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !control) {
        return null;
    }

    const password = control.parent.get("senha");
    const specialCharacterRegex = new RegExp("^(?=.*[!@#$%^&*])");

    if (specialCharacterRegex.test(password.value)) return null;

    return { notSpecialCharacter: true };
};
